import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  QrCodeIcon,
  ShieldCheckIcon,
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import {
  get2FAStatus,
  setupAuthenticator,
  setupEmail,
  setupSMS,
  verifySetup,
  disableMethod,
  generateBackupCodes,
  type TwoFactorStatus,
  type AuthenticatorSetupResponse
} from '@/api/twoFactorApi'
import toast from 'react-hot-toast'

interface TwoFactorSetupProps {
  onClose?: () => void
}

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({ onClose }) => {
  const [status, setStatus] = useState<TwoFactorStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [setupMethod, setSetupMethod] = useState<'authenticator' | 'email' | 'sms' | null>(null)
  const [authenticatorData, setAuthenticatorData] = useState<AuthenticatorSetupResponse | null>(null)
  const [verificationCode, setVerificationCode] = useState('')
  const [showBackupCodes, setShowBackupCodes] = useState(false)
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      setLoading(true)
      const statusData = await get2FAStatus()
      setStatus(statusData)
    } catch (error) {
      console.error('Failed to fetch 2FA status:', error)
      toast.error('Failed to load 2FA settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSetupMethod = async (method: 'authenticator' | 'email' | 'sms') => {
    try {
      setSetupMethod(method)
      
      if (method === 'authenticator') {
        const data = await setupAuthenticator()
        setAuthenticatorData(data)
        toast.success('Please scan the QR code with your authenticator app')
      } else if (method === 'email') {
        await setupEmail()
        toast.success('Verification code sent to your email')
      } else if (method === 'sms') {
        await setupSMS()
        toast.success('Verification code sent to your phone')
      }
    } catch (error: any) {
      console.error(`Failed to setup ${method} 2FA:`, error)
      toast.error(error.response?.data?.message || `Failed to setup ${method} 2FA`)
      setSetupMethod(null)
    }
  }

  const handleVerifySetup = async () => {
    if (!setupMethod || !verificationCode.trim()) return

    try {
      setVerifying(true)
      const result = await verifySetup(setupMethod, verificationCode.trim())
      
      if (result.success) {
        toast.success(result.message)
        setSetupMethod(null)
        setVerificationCode('')
        setAuthenticatorData(null)
        await fetchStatus()
        
        // Show backup codes for authenticator setup
        if (setupMethod === 'authenticator' && authenticatorData?.backupCodes) {
          setShowBackupCodes(true)
        }
      } else {
        toast.error(result.message)
      }
    } catch (error: any) {
      console.error('Failed to verify 2FA setup:', error)
      toast.error(error.response?.data?.message || 'Failed to verify 2FA setup')
    } finally {
      setVerifying(false)
    }
  }

  const handleDisableMethod = async (method: string) => {
    if (!confirm(`Are you sure you want to disable ${method} 2FA? This will reduce your account security.`)) {
      return
    }

    try {
      const result = await disableMethod(method)
      toast.success(result.message)
      await fetchStatus()
    } catch (error: any) {
      console.error(`Failed to disable ${method} 2FA:`, error)
      toast.error(error.response?.data?.message || `Failed to disable ${method} 2FA`)
    }
  }

  const handleGenerateBackupCodes = async () => {
    try {
      const result = await generateBackupCodes()
      setAuthenticatorData(prev => prev ? { ...prev, backupCodes: result.backupCodes } : null)
      setShowBackupCodes(true)
      toast.success('New backup codes generated')
    } catch (error: any) {
      console.error('Failed to generate backup codes:', error)
      toast.error(error.response?.data?.message || 'Failed to generate backup codes')
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    toast.success('Copied to clipboard!')
  }

  const getMethodIcon = (method: string) => {
    switch (method) {
      case 'authenticator':
        return QrCodeIcon
      case 'email':
        return EnvelopeIcon
      case 'sms':
        return DevicePhoneMobileIcon
      default:
        return ShieldCheckIcon
    }
  }

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'authenticator':
        return 'from-green-500 to-emerald-500'
      case 'email':
        return 'from-blue-500 to-indigo-500'
      case 'sms':
        return 'from-purple-500 to-pink-500'
      default:
        return 'from-gray-500 to-gray-600'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Two-Factor Authentication</h3>
          <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Current Status */}
      {status && (
        <div className={`p-4 rounded-lg border ${
          status.enabled 
            ? 'bg-green-50 border-green-200' 
            : 'bg-yellow-50 border-yellow-200'
        }`}>
          <div className="flex items-center space-x-2">
            {status.enabled ? (
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            )}
            <span className={`font-medium ${
              status.enabled ? 'text-green-800' : 'text-yellow-800'
            }`}>
              {status.enabled ? 'Two-Factor Authentication is enabled' : 'Two-Factor Authentication is disabled'}
            </span>
          </div>
          {status.enabled && (
            <p className="text-sm text-green-700 mt-1">
              Your account is protected with {status.methods.filter(m => m.status === 'active').length} active 2FA method(s)
            </p>
          )}
        </div>
      )}

      {/* Setup Method Selection */}
      {!setupMethod && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              id: 'authenticator',
              title: 'Authenticator App',
              description: 'Use apps like Google Authenticator, Authy, or 1Password',
              icon: QrCodeIcon,
              color: 'from-green-500 to-emerald-500',
              recommended: true
            },
            {
              id: 'email',
              title: 'Email Verification',
              description: 'Receive verification codes via email',
              icon: EnvelopeIcon,
              color: 'from-blue-500 to-indigo-500',
              recommended: false
            },
            {
              id: 'sms',
              title: 'SMS Verification',
              description: 'Receive verification codes via SMS',
              icon: DevicePhoneMobileIcon,
              color: 'from-purple-500 to-pink-500',
              recommended: false
            }
          ].map((method) => {
            const IconComponent = method.icon
            const isActive = status?.methods.some(m => m.method === method.id && m.status === 'active')
            
            return (
              <motion.div
                key={method.id}
                whileHover={{ y: -4 }}
                className={`relative p-6 border rounded-xl cursor-pointer transition-all ${
                  isActive 
                    ? 'border-green-300 bg-green-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => isActive ? null : handleSetupMethod(method.id as any)}
              >
                {method.recommended && (
                  <div className="absolute -top-2 -right-2">
                    <span className="bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                      Recommended
                    </span>
                  </div>
                )}
                
                <div className="flex flex-col items-center text-center">
                  <div className={`w-12 h-12 bg-gradient-to-r ${method.color} rounded-xl flex items-center justify-center mb-4`}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">{method.title}</h4>
                  <p className="text-sm text-gray-600 mb-4">{method.description}</p>
                  
                  {isActive ? (
                    <div className="w-full">
                      <div className="flex items-center justify-center text-green-600 mb-3">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        <span className="text-sm font-medium">Active</span>
                      </div>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDisableMethod(method.id)
                        }}
                        variant="secondary"
                        size="sm"
                        className="w-full text-red-600 hover:text-red-700"
                      >
                        Disable
                      </Button>
                    </div>
                  ) : (
                    <Button className="w-full">
                      Setup {method.title}
                    </Button>
                  )}
                </div>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Active Methods List */}
      {status?.methods.filter(m => m.status === 'active').length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="font-medium text-gray-900 mb-4">Active 2FA Methods</h4>
          <div className="space-y-3">
            {status.methods.filter(m => m.status === 'active').map((method) => {
              const IconComponent = getMethodIcon(method.method)
              
              return (
                <div key={method.method} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 bg-gradient-to-r ${getMethodColor(method.method)} rounded-lg flex items-center justify-center`}>
                      <IconComponent className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{method.displayName}</p>
                      <p className="text-xs text-gray-500">
                        {method.enabledAt && `Enabled ${new Date(method.enabledAt).toLocaleDateString()}`}
                        {method.lastUsedAt && ` • Last used ${new Date(method.lastUsedAt).toLocaleDateString()}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {method.method === 'authenticator' && (
                      <Button
                        onClick={handleGenerateBackupCodes}
                        variant="secondary"
                        size="sm"
                      >
                        <KeyIcon className="h-4 w-4 mr-1" />
                        Backup Codes
                      </Button>
                    )}
                    <Button
                      onClick={() => handleDisableMethod(method.method)}
                      variant="secondary"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                    >
                      Disable
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Authenticator Setup */}
      {setupMethod === 'authenticator' && authenticatorData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border rounded-xl p-6"
        >
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <QrCodeIcon className="h-5 w-5 text-green-600 mr-2" />
            Setup Authenticator App
          </h4>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* QR Code */}
            <div className="text-center">
              <div className="bg-white p-4 rounded-lg border inline-block">
                <img 
                  src={authenticatorData.qrCodeUrl} 
                  alt="2FA QR Code" 
                  className="w-48 h-48"
                />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Scan this QR code with your authenticator app
              </p>
            </div>

            {/* Manual Entry */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Manual Entry Key
                </label>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 p-3 bg-gray-100 rounded font-mono text-sm break-all">
                    {authenticatorData.manualEntryKey}
                  </code>
                  <Button
                    onClick={() => copyToClipboard(authenticatorData.manualEntryKey)}
                    variant="secondary"
                    size="sm"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification Code
                </label>
                <Input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="text-center text-lg tracking-wider"
                  maxLength={6}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <QrCodeIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h5 className="text-sm font-medium text-blue-900">Setup Instructions</h5>
                    <ol className="text-sm text-blue-700 mt-1 space-y-1">
                      <li>1. Install an authenticator app (Google Authenticator, Authy, 1Password)</li>
                      <li>2. Scan the QR code or enter the manual key</li>
                      <li>3. Enter the 6-digit code from your app</li>
                    </ol>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <Button
                  onClick={() => setSetupMethod(null)}
                  variant="secondary"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleVerifySetup}
                  disabled={verificationCode.length !== 6 || verifying}
                  className="flex-1"
                >
                  {verifying ? 'Verifying...' : 'Verify & Enable'}
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Email/SMS Setup */}
      {(setupMethod === 'email' || setupMethod === 'sms') && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border rounded-xl p-6"
        >
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            {setupMethod === 'email' ? (
              <EnvelopeIcon className="h-5 w-5 text-blue-600 mr-2" />
            ) : (
              <DevicePhoneMobileIcon className="h-5 w-5 text-purple-600 mr-2" />
            )}
            Setup {setupMethod === 'email' ? 'Email' : 'SMS'} Verification
          </h4>
          
          <div className="max-w-md mx-auto space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Verification Code
              </label>
              <Input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="text-center text-lg tracking-wider"
                maxLength={6}
              />
            </div>

            <div className={`${setupMethod === 'email' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'} border rounded-lg p-4`}>
              <div className="flex items-start space-x-3">
                <ClockIcon className={`h-5 w-5 mt-0.5 ${setupMethod === 'email' ? 'text-blue-600' : 'text-purple-600'}`} />
                <div>
                  <h5 className={`text-sm font-medium ${setupMethod === 'email' ? 'text-blue-900' : 'text-purple-900'}`}>
                    Verification Code Sent
                  </h5>
                  <p className={`text-sm mt-1 ${setupMethod === 'email' ? 'text-blue-700' : 'text-purple-700'}`}>
                    We've sent a 6-digit verification code to your {setupMethod === 'email' ? 'email address' : 'phone number'}. 
                    The code will expire in 10 minutes.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <Button
                onClick={() => setSetupMethod(null)}
                variant="secondary"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleVerifySetup}
                disabled={verificationCode.length !== 6 || verifying}
                className="flex-1"
              >
                {verifying ? 'Verifying...' : 'Verify & Enable'}
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Backup Codes Modal */}
      <AnimatePresence>
        {showBackupCodes && authenticatorData?.backupCodes && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowBackupCodes(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Backup Codes</h3>
                    <p className="text-sm text-gray-500">Save these codes in a secure location</p>
                  </div>
                  <button
                    onClick={() => setShowBackupCodes(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mt-0.5" />
                    <div>
                      <h5 className="text-sm font-medium text-yellow-900">Important!</h5>
                      <p className="text-sm text-yellow-700 mt-1">
                        These backup codes can be used to access your account if you lose access to your authenticator app. 
                        Each code can only be used once. Store them in a safe place!
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {authenticatorData.backupCodes.map((code, index) => (
                    <button
                      key={index}
                      onClick={() => copyToClipboard(code)}
                      className="p-3 bg-gray-100 rounded-lg font-mono text-sm hover:bg-gray-200 transition-colors text-center"
                    >
                      {code}
                    </button>
                  ))}
                </div>

                <div className="flex justify-between pt-4">
                  <Button
                    onClick={() => copyToClipboard(authenticatorData.backupCodes.join('\n'))}
                    variant="secondary"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                    Copy All
                  </Button>
                  <Button
                    onClick={() => setShowBackupCodes(false)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    I've Saved My Codes
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default TwoFactorSetup